import cntl from 'cntl'
import { GatsbyImage, getImage, ImageDataLike } from 'gatsby-plugin-image'
import React from 'react'
import { SectionWrapper } from '../Pricing'
import { ITestimonial } from 'types'
import slugify from 'slugify'

const sizes = {
    md: {
        quote: cntl`
            md:text-[24px]
            md:leading-[33.6px]
            m-0
        `,
        name: cntl`
            md:text-[20px]
            md:leading-[21.88px]
        `,
        title: cntl`
            md:text-[18px]
        `,
        image: cntl`
            w-[80px]
            h-[80px]
        `,
        footer: cntl`
            mt-[20px]
            space-x-[14px]
        `,
    },
    lg: {
        quote: cntl`
            md:text-[32px]
            md:leading-[48px]
        `,
        name: cntl`
            md:text-xl
        `,
        title: cntl`
            md:text-lg
        `,
        image: cntl`
            w-[100px]
            h-[100px]
        `,
        footer: cntl`
            space-x-8
            mt-9
        `,
    },
}

const base = {
    quote: cntl`
        text-xl
        text-primary
        dark:text-primary-dark
        m-0
        quote
    `,
    name: cntl`
        not-italic
        text-lg
    `,
    title: cntl`
        not-italic
        text-lg
        opacity-50
    `,
    footer: cntl`
        flex
        items-center
    `,
    section: cntl`
        !max-w-screen-lg
        mx-auto
        text-primary
        font-bold
    `,
    image: cntl`
        rounded-full
    `,
}

const classes = (type, size = 'lg', className = '') => cntl`
    ${base[type]}
    ${sizes[size][type]}
    ${className}
`

interface Props {
    quote: string | JSX.Element
    name: string | JSX.Element
    title: string | JSX.Element
    pricingAuthorImage: JSX.Element
    className?: string
    imageSource?: string
    size?: string
    logo?: string
}

export const Quote2 = ({
    quote,
    name,
    title,
    pricingAuthorImage,
    className = '',
    imageSource,
    size = 'lg',
    logo,
}: Props) => {
    return (
        <section className={classes('section', size, className)}>
            {logo && <img src={logo} className="mb-9" />}
            <blockquote className={classes('quote', size)}>
                {quote}
                <footer className={classes('footer', size)}>
                    {imageSource ? <img className={classes('image', size)} src={imageSource} /> : pricingAuthorImage}
                    <span className="flex flex-col">
                        <cite className={classes('name', size)}>{name}</cite>
                        <cite className={classes('title', size)}>{title}</cite>
                    </span>
                </footer>
            </blockquote>
        </section>
    )
}

function PricingQuote({ children, className = '' }: { children: React.ReactNode[]; className?: string }) {
    return (
        <div
            className={`-mx-5 grid gap-y-8 bg-gray-accent-light py-10 px-5 md:-mx-12 md:grid-cols-2 md:gap-y-0 md:gap-x-8 md:px-16 lg:gap-x-12 xl:gap-x-16 ${className}`}
        >
            <div>{children[0]}</div>
            <div className="-mt-16">{children[1]}</div>
        </div>
    )
}

export const Quote = (props) => {
    const {
        googleImages,
        productTestimonial,
        pricingQuote,
        name,
        title,
        pricingAuthorImage,
        className = '',
        imageSource,
        size = 'lg',
        logo,
    } = props
    if (Array.isArray(productTestimonial) && productTestimonial.length == 1) productTestimonial = productTestimonial[0]
    const author = productTestimonial?.author
    const enable = productTestimonial?.enable
    const image = productTestimonial?.image
    const quote = productTestimonial?.quote || pricingQuote
    const imageFile = productTestimonial?.imageFile
    if (enable?.toLowerCase?.() === 'false' && !process.env.RENDER_DISABLED_COMPONENTS) {
        return <></>
    }
    const googleImage =
        image &&
        googleImages &&
        googleImages.filter((img) => {
            if (img.name == image) {
                return img
            }
        })[0]
    const gatsbImage = (googleImage && getImage(googleImage)) || (imageFile && getImage(imageFile))
    const authorCompanyName = author?.company?.name || author?.companyName
    const googleAuthorCompanyImage = (
        author?.companyImage &&
        googleImages &&
        googleImages.filter((img) => {
            if (img.name == author?.companyImage) {
                return img
            }
        })?.[0]
    )?.publicURL
    const authorCompanyImage = googleAuthorCompanyImage || logo
    const googleAuthorImage = (
        author?.image &&
        googleImages &&
        googleImages.filter((img) => {
            if (img.name == author?.image) {
                return img
            }
        })?.[0]
    )?.publicURL
    const authorImage = googleAuthorImage || pricingAuthorImage
    return (
        <SectionWrapper className={classes('section', size, className)}>
            <div>
                {authorCompanyImage && <img className="mb-9" src={authorCompanyImage} />}
                <blockquote className={classes('quote', size)}>
                    {quote && <p dangerouslySetInnerHTML={{ __html: quote }} />}
                    <footer className={classes('footer', size)}>
                        {authorImage ? (
                            <img
                                className={classes('image', size)}
                                width={100}
                                height={100}
                                placeholder="none"
                                alt={`${author?.name} ${author?.role || title}, ${authorCompanyName}`}
                                src={authorImage}
                            />
                        ) : (
                            pricingAuthorImage
                        )}
                        <span className="flex flex-col">
                            {author?.name && <cite className={classes('name', size)}>{author?.name || name}</cite>}
                            {author?.role && authorCompanyName && (
                                <cite className={classes('title', size)}>
                                    {' '}
                                    {author?.role || title}, {authorCompanyName}
                                </cite>
                            )}
                        </span>
                    </footer>
                </blockquote>
            </div>
            {/* <div>
                    {gatsbImage && <GatsbyImage alt="" image={gatsbImage} />}
                </div> */}
        </SectionWrapper>
    )
}
